// platform
import { useEffect } from 'react';
import { atomWithImmer } from 'jotai-immer';
import { useAtom } from 'jotai';

// app
import utilSvc from '../../../core/utilSvc';

// types
import { UserInfo } from '../../../../global';
import { BiMenuItem, BiReportItem } from '../types';

// menu schema
import { BI_MENU_SCHEMA } from './menuSchema';

// STORE TYPES
export type BiMenuData = {
    items: BiMenuItem[];
    activeReport: BiReportItem | null;
};
export type BiMenuSetResult = {
    itemCount: number;
    activeReport: BiReportItem | null;
};
type BiMenuMethods = {
    setMenu: (user: UserInfo | null, bizModels: string[], lastReport: string | null) => BiMenuSetResult;
    setActiveReport: (rpt: BiReportItem | null) => void;
    unload: () => void;
};
export type BiMenuStore = BiMenuData & BiMenuMethods;

// STORE DATA
const biMenuAtom = atomWithImmer<BiMenuData>(storeDataVM());
function storeDataVM(): BiMenuData {
    return {
        items: [],
        activeReport: null,
    };
}

// STORE HOOK
const useMenuStore = (): BiMenuStore => {
    const [store, setStore] = useAtom(biMenuAtom);

    // INIT
    useEffect(() => {
        console.log('BI MENU STORE: INIT');

        // destroy
        return (): void => {
            console.log('BI MENU STORE: DESTROY with items: ' + store.items.length);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // DEFINITION
    return {
        items: store.items,
        activeReport: store.activeReport,
        setMenu,
        setActiveReport,
        unload,
    };

    function setMenu(user: UserInfo | null, bizModels: string[], lastReportClaim: string | null): BiMenuSetResult {
        // setup store menu items with user rx models and last report used

        const result: BiMenuSetResult = {
            itemCount: 0,
            activeReport: null,
        };
        const menuItems: BiMenuItem[] = [];

        // no user or feature, no menu
        if (!user || !utilSvc.user.hasFeature(user, 'report.bi')) {
            return result;
        }

        const menu = BI_MENU_SCHEMA;
        let newSection: BiMenuItem,
            bizModelOk = false;

        // loop menu sections
        menu.forEach(section => {
            newSection = { title: section.title, reports: [] };

            // loop reports
            section.reports.forEach(rpt => {
                if (utilSvc.user.hasFeature(user, rpt.claim)) {
                    // biz models check
                    bizModelOk = rpt.rxmodels.length < 1 ? true : rpt.rxmodels.some(b => bizModels.indexOf(b) > -1);

                    if (bizModelOk) {
                        // add to menu
                        newSection.reports.push(rpt);

                        // matches last report?
                        if (!!lastReportClaim && rpt.claim == lastReportClaim) {
                            result.activeReport = rpt;
                        }
                    }
                }
            });

            // add section?
            if (newSection.reports.length > 0) {
                menuItems.push(newSection);
            }
        });

        setStore(draft => {
            draft.items = menuItems;
            draft.activeReport = result.activeReport;
        });

        result.itemCount = menuItems.length;
        return result;
    }

    function setActiveReport(rpt: BiReportItem | null): void {
        console.log('menu store set active report');

        setStore(draft => {
            draft.activeReport = rpt;
        });
    }

    // UNLOAD
    function unload(): void {
        console.log('BI MENU STORE: UNLOAD: ' + store.items.length);
        setStore(() => storeDataVM()); // hard reset, do not use draft
    }
};

export default useMenuStore;
